// AI Inference System - Base class for AI inference operations
// Handles streaming responses, tool execution, and conversation management
// Owns a Chat instance for message storage

import { Chat } from "../chat/Chat.js";
import { logger } from "../../../core/logger.js";
import { repairPartialToolArgJson } from "../utils/repairPartialToolArgJson.js";
import {
  REVOLT_ERROR_CODES,
  normalizeInferenceError,
  errorFromEnvelope,
} from "../utils/inferenceErrorEnvelope.js";
import { REVOLT_HALT_AFTER_ASSISTANT_TOOL_BATCH_KEYS } from "../constants/revoltInferenceToolDirectives.js";

/**
 * @param {{ requestOptions?: Record<string, unknown> } | null | undefined} context
 * @returns {number|null}
 */
function getMaxOutputTokensFromInferenceContext(context) {
  const ro = context?.requestOptions;
  if (!ro || typeof ro !== "object") return null;
  const v =
    "max_tokens" in ro ? ro.max_tokens : "maxTokens" in ro ? ro.maxTokens : null;
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/**
 * Model-oriented explanation when tool-call JSON was cut mid-stream (usually
 * max output tokens). Avoids parser jargon like "unterminated string" in text
 * the model is meant to act on; keep repair `reason` on the truncation object
 * for logs / optional tool use only.
 *
 * @param {{ maxOutputTokens: number | null }} opts
 * @returns {string}
 */
function buildToolCallArgumentsTruncationSummary(opts) {
  const { maxOutputTokens } = opts;
  const limitClause =
    maxOutputTokens != null
      ? `This turn was limited to ${maxOutputTokens} output tokens; the response ran out of room while still streaming the tool call. `
      : `This turn hit its output token budget before the tool call finished streaming. `;
  return (
    limitClause +
    "That stopped the tool's JSON partway through — often inside a long string such as file `content` — so only the beginning of the arguments arrived. " +
    "This is not a separate JSON-serialization bug; the payload was too large to fit in one assistant message."
  );
}

export class AIInferenceSystem {
  constructor() {
    // Own the Chat instance
    this.chat = new Chat();

    // Quadrad peer references (Tool + AI Tool + Bubble UI; wired with inference)
    this.toolSystem = null; // Reference to ToolSystem
    this.aiToolSystem = null; // Reference to AIToolSystem
    this.bubbleUiSystem = null; // Reference to BubbleUiSystem

    // Event listeners for UI integration
    this.eventListeners = new Map();

    // Active inference contexts (for cancellation support)
    this.activeContexts = new Set();

    // Checkpoint tracking (response-scoped)
    this.checkpointEntriesByResponse = new Map(); // responseMessageId -> Map(entryId -> entry)
    this.activeToolContext = null;
    this.activeRedoCheckpointId = null;
    this.activeRedoEntriesById = null;

    // Bind methods
    this._handleChunkReceived = this._handleChunkReceived.bind(this);
  }

  // ==========================================
  // EVENT SYSTEM
  // ==========================================

  /**
   * Add an event listener
   * @param {string} eventName - Event name (onBeginSegment, onUpdateSegment, onToolCallFunctionKnown, onStreamStart, onStreamIdle, onInitialConnectionPhaseEnd, onAbnormalTermination, onInferenceFinished, etc.)
   * @param {Function} listener - Event listener function
   */
  addEventListener(eventName, listener) {
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, new Set());
    }
    this.eventListeners.get(eventName).add(listener);
  }

  /**
   * Remove an event listener
   * @param {string} eventName - Event name
   * @param {Function} listener - Event listener function
   */
  removeEventListener(eventName, listener) {
    if (this.eventListeners.has(eventName)) {
      this.eventListeners.get(eventName).delete(listener);
    }
  }

  /**
   * Remove all event listeners
   */
  removeAllListeners() {
    this.eventListeners.clear();
  }

  /**
   * Get the instance identifier for this AI inference system
   * Subclasses should override this to return their specific instance ID
   * @returns {string} Instance identifier (e.g., "workspace", "app-creator")
   */
  getInstanceId() {
    // Default fallback - subclasses should override
    return "unknown";
  }

  /**
   * Emit an event to all listeners
   * @param {string} eventName - Event name
   * @param {...any} args - Arguments to pass to listeners
   */
  _emit(eventName, ...args) {
    if (this.eventListeners.has(eventName)) {
      for (const listener of this.eventListeners.get(eventName)) {
        try {
          listener(...args);
        } catch (error) {
          logger.error(
            {
              tags: "ai-inference|event-error",
              color1: "red",
            },
            `Error in ${eventName} listener:`,
            error
          );
        }
      }
    }
  }

  /**
   * Set the tool system reference
   * @param {ToolSystem} toolSystem - The tool system instance
   */
  setToolSystem(toolSystem) {
    this.toolSystem = toolSystem;
  }

  /**
   * Set the AI tool system reference
   * @param {AIToolSystem} aiToolSystem - The AI tool system instance
   */
  setAIToolSystem(aiToolSystem) {
    this.aiToolSystem = aiToolSystem;
  }

  /**
   * Set the bubble UI system reference
   * @param {BubbleUiSystem} bubbleUiSystem - The bubble UI system instance
   */
  setBubbleUiSystem(bubbleUiSystem) {
    this.bubbleUiSystem = bubbleUiSystem;
  }

  /**
   * Add a message to the chat (controlled external interface)
   * @param {Object} messageData - Message data object
   * @returns {string} Message ID
   */
  addMessage(messageData) {
    const messageId = this.chat.addMessage(messageData);
    this.onChatModified('addMessage', { messageId, messageData });
    return messageId;
  }

  /**
   * Clear the chat
   */
  clearChat() {
    this.chat.clearMessages();
    this.onChatModified('clear');
  }

  /**
   * Create a new chat instance (replaces the current chat)
   */
  newChat() {
    // Save the old chat if needed (but don't trigger save here since we're just replacing)
    this.chat = new Chat();
    this.onChatModified('newChat');
  }

  /**
   * Called after chat is structurally modified
   * Override in subclasses for persistence logic
   * @param {string} operation - Type of operation
   * @param {Object} data - Additional operation data
   */
  onChatModified(operation, data = {}) {
    // Base implementation - override in subclasses
  }

  // ==========================================
  // CHAT INSTANCE TOOL DATA (per-namespace bag)
  // ==========================================
  //
  // Thin wrappers around the Chat instance's tool-data bag that also
  // notify onChatModified so subclasses (e.g. WorkspaceAIInferenceSystem)
  // can persist the chat after a tool mutates the bag.

  /**
   * Read namespace-scoped instance tool data.
   * @param {string} namespace
   * @returns {*}
   */
  getInstanceToolData(namespace) {
    return this.chat?.getInstanceToolData?.(namespace) ?? null;
  }

  /**
   * Write namespace-scoped instance tool data.
   * @param {string} namespace
   * @param {*} value
   * @returns {boolean}
   */
  setInstanceToolData(namespace, value) {
    if (!this.chat) return false;
    const ok = this.chat.setInstanceToolData(namespace, value);
    if (ok) {
      this.onChatModified("instanceToolData", { namespace });
    }
    return ok;
  }

  /**
   * Remove a namespace from instance tool data.
   * @param {string} namespace
   * @returns {boolean}
   */
  deleteInstanceToolData(namespace) {
    if (!this.chat) return false;
    const ok = this.chat.deleteInstanceToolData(namespace);
    if (ok) {
      this.onChatModified("instanceToolData", { namespace, deleted: true });
    }
    return ok;
  }

  /**
   * Read tool-call-scoped instance tool data within a namespace.
   * @param {string} namespace
   * @param {string} toolCallId
   * @returns {*}
   */
  getInstanceToolCallData(namespace, toolCallId) {
    return (
      this.chat?.getInstanceToolCallData?.(namespace, toolCallId) ?? null
    );
  }

  /**
   * Write tool-call-scoped instance tool data within a namespace.
   * @param {string} namespace
   * @param {string} toolCallId
   * @param {*} value
   * @returns {boolean}
   */
  setInstanceToolCallData(namespace, toolCallId, value) {
    if (!this.chat) return false;
    const ok = this.chat.setInstanceToolCallData(namespace, toolCallId, value);
    if (ok) {
      this.onChatModified("instanceToolData", { namespace, toolCallId });
    }
    return ok;
  }

  /**
   * Remove a tool-call-scoped entry within a namespace.
   * @param {string} namespace
   * @param {string} toolCallId
   * @returns {boolean}
   */
  deleteInstanceToolCallData(namespace, toolCallId) {
    if (!this.chat) return false;
    const ok = this.chat.deleteInstanceToolCallData(namespace, toolCallId);
    if (ok) {
      this.onChatModified("instanceToolData", {
        namespace,
        toolCallId,
        deleted: true,
      });
    }
    return ok;
  }

  // ==========================================
  // CONTEXT MANAGEMENT
  // ==========================================

  /**
   * Create a new inference context
   * @param {string} modelId - Model identifier
   * @param {Object} options - Additional options
   * @returns {Object} Context object
   */
  _buildContextDefaults(modelId, options = {}) {
    return {
      // Core identifiers
      modelId,
      userId: options.userId || null, // Store user ID for frontend incident reporting and logging only (NOT sent to backend)
      messageId: null, // Will be set when message is created
      rootResponseMessageId: null, // Stable response root across recursion
      inferenceSessionId:
        options.inferenceSessionId ||
        `inf_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      hasEmittedInferenceFinished: false,
      totalSegmentsCount: 0,

      // Cancellation support
      cancelController: new AbortController(),
      cancelled: false,

      // Usage accumulation across recursive calls
      totalUsage: {
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
        cost: 0,
        reasoning_tokens: 0,
        cached_tokens: 0,
        audio_tokens: 0,
        video_tokens: 0,
        image_tokens: 0,
      },

      // Segment tracking (native implementation)
      segments: new Map(), // segmentId -> segment data
      currentSegmentId: null,

      // Tool call tracking
      pendingToolCalls: new Map(), // toolCallId -> tool call data
      completedToolCalls: new Map(), // toolCallId -> result

      // Tool call accumulation during streaming
      streamingToolCalls: new Map(), // toolCallIndex -> accumulated tool call data

      // Conversation state
      isToolExecutionPhase: false,
      recursionDepth: 0,

      // When true after `_executeToolCalls` / local tool dispatch: all tools in this
      // assistant wave still run to completion and tool `role` rows are written,
      // then **no** further model generation step is started (cloud: skip recursive
      // `infer`; local: forwarded on `toolExecutionComplete` for desktop).
      haltInferenceAfterAssistantToolBatch: false,

      // Streaming state
      streamingStatus: null, // null | "start" | "idle" | "data"
      isInitialConnectionPhase: true, // true until first data chunk received

      // Inference mode: "cloud" (Revolt site backend / OpenRouter) or "local" (RevoltDesktop / NodeLlamaCpp).
      // Preserved across recursive tool-call continuation.
      inferenceMode: options.mode ?? "cloud",

      // Additional context data
      ...options,
    };
  }

  _createContext(modelId, options = {}) {
    const context = this._buildContextDefaults(modelId, options);

    this.activeContexts.add(context);
    return context;
  }

  _ensureContextDefaults(context, modelId, options = {}) {
    if (!context || typeof context !== "object") {
      return this._createContext(modelId, options);
    }

    const defaults = this._buildContextDefaults(modelId, options);
    for (const [key, value] of Object.entries(defaults)) {
      if (context[key] === undefined) {
        context[key] = value;
      }
    }

    return context;
  }

  /**
   * Clean up a context
   * @param {Object} context - Context to clean up
   */
  _cleanupContext(context) {
    this._cleanupLocalListeners(context);
    this.activeContexts.delete(context);
    if (context.cancelController) {
      context.cancelController.abort();
    }
  }

  // ==========================================
  // CHECKPOINT TRACKING
  // ==========================================

  _getResponseEntryMap(responseMessageId, createIfMissing = false) {
    if (!responseMessageId) {
      return null;
    }
    let map = this.checkpointEntriesByResponse.get(responseMessageId);
    if (!map && createIfMissing) {
      map = new Map();
      this.checkpointEntriesByResponse.set(responseMessageId, map);
    }
    return map;
  }

  getCurrentResponseMessageId() {
    return (
      this.activeToolContext?.rootResponseMessageId ||
      this.activeToolContext?.messageId ||
      null
    );
  }

  hasCheckpointEntry(entryId, responseMessageId = null) {
    const responseId =
      responseMessageId || this.getCurrentResponseMessageId();
    const map = this._getResponseEntryMap(responseId, false);
    return !!map?.has(entryId);
  }

  createCheckpointEntry(entryId, entry, responseMessageId = null) {
    const responseId =
      responseMessageId || this.getCurrentResponseMessageId();
    const map = this._getResponseEntryMap(responseId, true);
    if (!map) {
      return false;
    }
    if (map.has(entryId)) {
      return false;
    }
    map.set(entryId, entry);
    return true;
  }

  getEntriesForResponse(responseMessageId) {
    const map = this._getResponseEntryMap(responseMessageId, false);
    return map ? new Map(map) : new Map();
  }

  clearResponseEntries(responseMessageId) {
    if (!responseMessageId) {
      return;
    }
    this.checkpointEntriesByResponse.delete(responseMessageId);
  }

  createRedoCheckpointEntry(entryId, entry) {
    if (!this.activeRedoEntriesById) {
      return false;
    }
    if (this.activeRedoEntriesById.has(entryId)) {
      return false;
    }
    this.activeRedoEntriesById.set(entryId, entry);
    return true;
  }

  /**
   * Cancel an active inference
   * @param {Object} context - Context to cancel (optional - cancels all if not provided)
   */
  cancel(context = null) {
    if (context) {
      context.cancelled = true;
      this._emitToolCallCleanupForCancelled(context);
      if (context.cancelController) {
        context.cancelController.abort();
      }
    } else {
      // Cancel all active contexts
      for (const activeContext of this.activeContexts) {
        activeContext.cancelled = true;
        this._emitToolCallCleanupForCancelled(activeContext);
        if (activeContext.cancelController) {
          activeContext.cancelController.abort();
        }
      }
    }
  }

  _emitToolCallCleanupForCancelled(context) {
    if (!context) {
      return;
    }

    const pendingToolCallIds = new Set();
    const executingToolCallIds = new Set();
    const toolCallIds = new Set();

    for (const toolCall of context.pendingToolCalls.values()) {
      const isCompleted = context.completedToolCalls.has(toolCall.id);
      if (!isCompleted) {
        const status = toolCall.status || "pending";
        toolCallIds.add(toolCall.id);
        if (status === "executing") {
          executingToolCallIds.add(toolCall.id);
        } else {
          pendingToolCallIds.add(toolCall.id);
        }
      }
    }

    for (const accumulated of context.streamingToolCalls.values()) {
      if (accumulated?.id) {
        toolCallIds.add(accumulated.id);
        pendingToolCallIds.add(accumulated.id);
      }
    }

    if (toolCallIds.size === 0) {
      return;
    }

    for (const toolCallId of toolCallIds) {
      const toolCall = context.pendingToolCalls.get(toolCallId);
      if (toolCall) {
        context.pendingToolCalls.delete(toolCallId);
      }
    }

    context.streamingToolCalls.clear();

    this._emit("onToolCallCleanup", context.messageId, {
      toolCallIds: Array.from(toolCallIds),
      pendingToolCallIds: Array.from(pendingToolCallIds),
      executingToolCallIds: Array.from(executingToolCallIds),
      reason: "cancelled",
    });
  }

  // ==========================================
  // REASONING CONFIGURATION
  // ==========================================

  /**
   * Validate reasoning configuration
   * @param {Object} reasoning - Reasoning configuration object
   * @param {string} reasoning.effort - Effort level
   * @param {boolean} reasoning.exclude - Whether to exclude reasoning
   */
  _validateReasoningConfig(reasoning) {
    if (!reasoning || typeof reasoning !== "object") {
      throw new Error("Reasoning configuration must be an object");
    }

    const validEfforts = ["none", "minimal", "low", "medium", "high", "xhigh"];
    if (reasoning.effort && !validEfforts.includes(reasoning.effort)) {
      throw new Error(
        `Invalid reasoning effort "${reasoning.effort}". Must be one of: ${validEfforts.join(", ")}`
      );
    }

    if (
      reasoning.exclude !== undefined &&
      typeof reasoning.exclude !== "boolean"
    ) {
      throw new Error("Reasoning exclude must be a boolean");
    }
  }

  // ==========================================
  // CHUNK PARSING
  // ==========================================

  /**
   * Parse an OpenRouter chunk and extract relevant data
   * @param {Object} chunk - Raw chunk from OpenRouter
   * @returns {Object} Parsed chunk data
   */
  _parseChunk(chunk) {
    // OpenRouter chat-completion chunks typically include a top-level `id`
    // like `gen-...` even before usage arrives. We use it for debugging
    // reliability around cancellation/usage capture.
    const generationId = chunk?.id ?? null;

    const choice = chunk.choices?.[0];
    if (!choice) {
      return { type: "empty", generationId };
    }

    const delta = choice.delta || {};
    const finishReason = choice.finish_reason;

    // Extract content types
    const content = delta.content || "";
    const reasoning = delta.reasoning || "";

    // Extract tool calls (if present)
    const toolCalls = delta.tool_calls || [];

    // Extract usage info (if present in final chunk)
    const usage = chunk.usage
      ? {
        prompt_tokens: chunk.usage.prompt_tokens || 0,
        completion_tokens: chunk.usage.completion_tokens || 0,
        total_tokens: chunk.usage.total_tokens || 0,
        cost: chunk.usage.cost || 0,
        ...chunk.usage,
      }
      : null;

    return {
      type: finishReason ? "final" : "content",
      content,
      reasoning,
      toolCalls,
      finishReason,
      usage,
      generationId,
    };
  }

  // ==========================================
  // SEGMENT PROCESSING (NATIVE IMPLEMENTATION)
  // ==========================================

  /**
   * Begin a new segment
   * @param {Object} context - Inference context
   * @param {string} segmentType - Type of segment (text, reasoning, tool_call, etc.)
   * @param {Object} segmentData - Additional segment data
   * @returns {string} Segment ID
   */
  _beginSegment(context, segmentType, segmentData = {}) {
    const segmentId = `segment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const segment = {
      id: segmentId,
      type: segmentType,
      content: "",
      capabilities: segmentData.capabilities || [],
      blocking: segmentData.blocking || false,
      startTimestamp: Date.now(),
      endTimestamp: null,
      formats: segmentData.formats || {},
      activeFormat: segmentData.activeFormat || null,
      ...segmentData,
    };

    context.segments.set(segmentId, segment);
    context.currentSegmentId = segmentId;
    context.totalSegmentsCount += 1;

    // Sync segments to ChatMessage
    this._syncSegmentsToChatMessage(context);

    // Emit event
    this._emit("onBeginSegment", context.messageId, segment);

    return segmentId;
  }

  /**
   * Update current segment with new content
   * @param {Object} context - Inference context
   * @param {string} content - Content to append
   */
  _updateSegment(context, content) {
    if (!context.currentSegmentId) {
      // If no current segment, begin a default text segment
      this._beginSegment(context, "text");
    }

    const segment = context.segments.get(context.currentSegmentId);
    if (segment) {
      // Store the delta (new content from this chunk)
      segment.delta = content;
      // Accumulate the full content
      segment.content += content;

      // Sync segments to ChatMessage
      this._syncSegmentsToChatMessage(context);

      // Emit event
      this._emit(
        "onUpdateSegment",
        context.messageId,
        context.currentSegmentId,
        segment
      );
    }
  }

  /**
   * End the current segment
   * @param {Object} context - Inference context
   * @param {string} segmentId - Optional specific segment ID to end
   */
  _endSegment(context, segmentId = null) {
    const targetSegmentId = segmentId || context.currentSegmentId;
    if (!targetSegmentId) return;

    const segment = context.segments.get(targetSegmentId);
    if (segment) {
      segment.endTimestamp = Date.now();

      // Sync segments to ChatMessage
      this._syncSegmentsToChatMessage(context);

      // Emit event
      this._emit("onEndSegment", context.messageId, targetSegmentId, segment);

      // Clear current segment if this was it
      if (targetSegmentId === context.currentSegmentId) {
        context.currentSegmentId = null;
      }

      // Notify that chat has been modified (segment completed)
      this.onChatModified('endSegment', { segmentId: targetSegmentId, segment });
    }
  }

  // ==========================================
  // TOOL CALL DETECTION AND EXECUTION
  // ==========================================

  /**
   * Accumulate tool calls from streaming chunks (don't execute yet)
   * @param {Object} context - Inference context
   * @param {Array} toolCalls - Tool calls from chunk
   * @returns {boolean} True if tool calls were accumulated
   */
  _accumulateStreamingToolCalls(context, toolCalls) {
    if (!toolCalls || toolCalls.length === 0) return false;

    // Process each tool call in the chunk
    for (const toolCall of toolCalls) {
      const index = toolCall.index;
      if (index === undefined || index === null) continue;

      // Get or create accumulated tool call for this index
      let accumulated = context.streamingToolCalls.get(index);
      if (!accumulated) {
        accumulated = {
          index,
          id: toolCall.id,
          function: {
            name: null,
            arguments: "",
          },
          timestamp: Date.now(),
          status: "accumulating",
        };
        context.streamingToolCalls.set(index, accumulated);
      }

      // Accumulate data from this chunk
      if (toolCall.id && !accumulated.id) {
        accumulated.id = toolCall.id;
      }

      if (toolCall.function) {
        // Accumulate function name (only present in first chunk)
        if (toolCall.function.name && !accumulated.function.name) {
          accumulated.function.name = toolCall.function.name;

          // Emit event when function name is first known (before arguments complete)
          this._emit("onToolCallFunctionKnown", context.messageId, {
            toolCallId: accumulated.id,
            functionName: toolCall.function.name,
            index: accumulated.index,
            status: "accumulating_arguments",
            timestamp: accumulated.timestamp,
          });
        }

        // Accumulate arguments (comes in fragments across chunks)
        const argsDelta = toolCall.function.arguments;
        if (typeof argsDelta === "string" && argsDelta.length > 0) {
          accumulated.function.arguments += argsDelta;

          // Forward per-argument-chunk UI streaming event (cloud + local parity)
          if (accumulated.id) {
            this._emit("onToolCallParamsChunk", context.messageId, {
              toolCallId: accumulated.id,
              functionName: accumulated.function.name,
              index: accumulated.index,
              paramsChunk: argsDelta,
              paramsTextSoFar: accumulated.function.arguments,
              done: false,
            });
          }
        }
      }
    }

    return toolCalls.length > 0;
  }

  /**
   * Convert accumulated streaming tool calls to pending tool calls for execution
   * @param {Object} context - Inference context
   * @returns {boolean} True if tool calls were processed and ready for execution
   */
  _finalizeStreamingToolCalls(context) {
    if (context.streamingToolCalls.size === 0) return false;

    // End any current segment before starting tool calls
    this._endSegment(context);

    // Convert accumulated tool calls to pending tool calls
    for (const [index, accumulated] of context.streamingToolCalls) {
      // Only process complete tool calls
      if (!accumulated.id || !accumulated.function.name) {
        logger.warn(
          {
            tags: "ai-inference|tool-call|warning",
            color1: "yellow",
          },
          `Incomplete tool call at index ${index}, skipping:`,
          accumulated
        );
        // If a bubble was already streamed (id is present but name never
        // arrived), land it in an explicit failed state rather than leaving
        // it orphaned. No-op when id is missing — no bubble exists to close.
        if (accumulated.id) {
          this._emit(
            "onToolCallCompleted",
            context.messageId,
            {
              id: accumulated.id,
              function: {
                name: accumulated.function.name || "unknown",
                arguments: {},
              },
              status: "failed",
              timestamp: accumulated.timestamp,
            },
            {
              success: false,
              error:
                "Malformed tool call: the response stream ended before a function name was received.",
            }
          );
        }
        continue;
      }

      // Parse accumulated arguments. If the model hit an output-token cap (or
      // the stream was otherwise cut short) mid-arguments, the raw buffer is
      // an unterminated JSON string. `repairPartialToolArgJson` closes it off
      // best-effort so the tool can still be dispatched with whatever was
      // received; tools that opt in (via BaseAITool.getCurrentToolCallTruncation)
      // branch on the truncation flag to surface the condition back to the model.
      const repairResult = repairPartialToolArgJson(
        accumulated.function.arguments
      );
      const parsedArgs = repairResult.parsed;

      if (repairResult.wasTruncated) {
        logger.warn(
          {
            tags: "ai-inference|tool-call|truncated",
            color1: "yellow",
          },
          `Tool call arguments for ${accumulated.function.name} were truncated (reason: ${repairResult.reason}); dispatching with best-effort repaired args.`,
          {
            toolCallId: accumulated.id,
            repairedKeys: Object.keys(parsedArgs || {}),
            rawLength: repairResult.rawArguments.length,
          }
        );
      }

      // Create pending tool call
      const toolCallData = {
        id: accumulated.id,
        function: {
          name: accumulated.function.name,
          arguments: parsedArgs,
        },
        timestamp: Date.now(),
        status: "pending",
        // In-memory only. Consumed by BaseAITool.getCurrentToolCallTruncation()
        // during tool.execute(); never serialized to Chat state and never sent
        // back to the provider as part of the tool-calls continuation payload
        // (the array built below only reads id/type/function.{name,arguments}).
        truncation: repairResult.wasTruncated
          ? (() => {
              const maxOutputTokens =
                getMaxOutputTokensFromInferenceContext(context);
              return {
                wasTruncated: true,
                // Internal repair diagnostic (logs / optional debugging only).
                reason: repairResult.reason,
                maxOutputTokens,
                summary: buildToolCallArgumentsTruncationSummary({
                  maxOutputTokens,
                }),
                rawArguments: repairResult.rawArguments,
              };
            })()
          : null,
      };

      context.pendingToolCalls.set(accumulated.id, toolCallData);

      // Begin tool call segment
      this._beginSegment(context, "tool_call", {
        toolCallId: accumulated.id,
        functionName: accumulated.function.name,
        arguments: parsedArgs,
      });

      // Emit tool call started event
      this._emit("onToolCallStarted", context.messageId, toolCallData);
    }

    // Update the AI message to include tool_calls (required for API continuation)
    const toolCallsArray = Array.from(context.pendingToolCalls.values()).map(
      (tc) => ({
        id: tc.id,
        type: "function",
        function: {
          name: tc.function.name,
          arguments: JSON.stringify(tc.function.arguments),
        },
      })
    );

    // Update the AI message with tool calls
    this.chat.updateMessage(context.messageId, {
      tool_calls: toolCallsArray,
    });

    // Clear accumulated tool calls
    context.streamingToolCalls.clear();

    // Emit event with finalized tool call data
    const finalizedToolCalls = Array.from(context.pendingToolCalls.values());
    this._emit("onToolCallsReady", context.messageId, finalizedToolCalls);

    return context.pendingToolCalls.size > 0;
  }

  /**
   * Strip reserved Revolt tool-result directives and detect halt-after-batch.
   * Directives are never JSON.stringified onto `role: "tool"` messages.
   *
   * @param {*} value
   * @returns {{ modelFacing: *, haltAfterAssistantToolBatch: boolean }}
   */
  _partitionToolExecuteResult(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return { modelFacing: value, haltAfterAssistantToolBatch: false };
    }
    let haltAfterAssistantToolBatch = false;
    const modelFacing = { ...value };
    for (const key of REVOLT_HALT_AFTER_ASSISTANT_TOOL_BATCH_KEYS) {
      if (
        Object.prototype.hasOwnProperty.call(modelFacing, key) &&
        modelFacing[key]
      ) {
        haltAfterAssistantToolBatch = true;
      }
      delete modelFacing[key];
    }
    return { modelFacing, haltAfterAssistantToolBatch };
  }

  /**
   * While a tool's `execute()` is running, request that **after every tool call in
   * the current assistant tool wave** has finished and persisted tool results to
   * `Chat`, inference must **not** continue with another model pass (same batch
   * semantics as returning `__revoltHaltAfterAssistantToolBatch` on the payload).
   *
   * Cloud: `_handleToolCallsAndRecurse` skips the recursive `infer()` call.
   * Local: each `toolExecutionComplete` includes `haltInferenceAfterAssistantToolBatch`
   * so Revolt Desktop can defer stopping until the full wave completes.
   */
  queueHaltInferenceAfterAssistantToolBatch() {
    const ctx = this.activeToolContext;
    if (ctx && typeof ctx === "object") {
      ctx.haltInferenceAfterAssistantToolBatch = true;
    }
  }

  /**
   * Execute pending tool calls
   * @param {Object} context - Inference context
   * @param {string} modelId - Model identifier
   * @returns {Promise<Array>} Array of tool results
   */
  async _executeToolCalls(context, modelId) {
    if (!this.aiToolSystem) {
      logger.error(
        {
          tags: "ai-inference|tool-call|error",
          color1: "red",
        },
        "No AI tool system available for tool execution - tool calls will not be executed",
        {
          pendingToolCallsCount: context.pendingToolCalls.size,
          modelId,
        }
      );
      return [];
    }

    const results = [];
    this.activeToolContext = context;

    try {
      for (const [toolCallId, toolCall] of context.pendingToolCalls) {
        try {
          toolCall.status = "executing";
          logger.log(
            {
              tags: "ai-inference|tool-execution",
              color1: "cyan",
            },
            `Executing tool: ${toolCall.function.name}`
          );

          // Execute the tool
          const argsValue = toolCall.function.arguments;
          const parsedArgs =
            typeof argsValue === "string" ? JSON.parse(argsValue) : argsValue;

          const result = await this.aiToolSystem.executeTool(
            toolCall.function.name,
            modelId,
            parsedArgs
          );

          const { modelFacing, haltAfterAssistantToolBatch } =
            this._partitionToolExecuteResult(result);
          context.haltInferenceAfterAssistantToolBatch =
            context.haltInferenceAfterAssistantToolBatch ||
            haltAfterAssistantToolBatch;

          // Store result
          const toolResult = {
            tool_call_id: toolCallId,
            content: JSON.stringify(modelFacing),
            success: true,
          };

          results.push(toolResult);
          context.completedToolCalls.set(toolCallId, toolResult);

          // Update tool call status
          toolCall.status = "completed";
          toolCall.result = modelFacing;

          // Emit tool call completed event
          this._emit("onToolCallCompleted", context.messageId, toolCall, modelFacing);

          // End tool call segment
          this._endSegment(context);
        } catch (error) {
          logger.error(
            {
              tags: "ai-inference|tool-execution|error",
              color1: "red",
            },
            `Tool execution failed for ${toolCall.function.name}:`,
            error
          );

          // Store error result
          const errorResult = {
            tool_call_id: toolCallId,
            content: JSON.stringify({ error: error.message }),
            success: false,
          };

          results.push(errorResult);
          context.completedToolCalls.set(toolCallId, errorResult);

          // Update tool call status
          toolCall.status = "failed";
          toolCall.error = error.message;

          // Emit tool call completed event (with error)
          this._emit("onToolCallCompleted", context.messageId, toolCall, {
            error: error.message,
          });

          // End tool call segment
          this._endSegment(context);
        }
      }
    } finally {
      this.activeToolContext = null;
    }

    // Clear pending tool calls
    context.pendingToolCalls.clear();

    return results;
  }

  /**
   * Sync context segments to the ChatMessage
   * @param {Object} context - Inference context
   */
  _syncSegmentsToChatMessage(context) {
    const message = this.chat.getMessage(context.messageId);
    if (message) {
      message.data.segments = Array.from(context.segments.values());
      message._updateAggregatedCapabilities();
    }
  }

  /**
   * Process content and determine segment actions
   * @param {Object} context - Inference context
   * @param {Object} parsedChunk - Parsed chunk data
   */
  _processContentForSegments(context, parsedChunk) {
    if (parsedChunk.type === "empty") return;

    const { content, reasoning, toolCalls } = parsedChunk;

    // Handle reasoning content (special segment type)
    if (reasoning) {
      if (
        !context.currentSegmentId ||
        context.segments.get(context.currentSegmentId)?.type !== "reasoning"
      ) {
        this._endSegment(context); // End any current segment
        this._beginSegment(context, "reasoning");
      }
      this._updateSegment(context, reasoning);
      return;
    }

    // Handle tool calls (special segment type)
    if (toolCalls.length > 0) {
      // Tool calls will be handled separately in tool detection logic
      return;
    }

    // Handle regular content
    if (content) {
      if (
        !context.currentSegmentId ||
        context.segments.get(context.currentSegmentId)?.type !== "text"
      ) {
        this._endSegment(context); // End any current segment
        this._beginSegment(context, "text");
      }
      this._updateSegment(context, content);
    }
  }

  // ==========================================
  // API INTEGRATION
  // ==========================================

  /**
   * Prepare inference payload (messages, tools, options) shared by cloud and local.
   * Uses getOpenRouterMessages() and getToolsForModel() so both modes start from the same data.
   * Cloud adds instance + requestId; local will convert messages to node-llama-cpp format and call window.* API.
   * @param {string} modelId - Model identifier
   * @param {Object} context - Context containing requestOptions
   * @returns {Promise<Object>} { model, messages, stream, tools?, ...requestOptions } (no instance)
   */
  async _prepareInferencePayload(modelId, context = {}) {
    const messages = this.chat.getOpenRouterMessages();
    const hasStructuredOutputs =
      context.requestOptions?.response_format?.type === "json_schema";

    const payload = {
      model: modelId,
      messages,
      stream: true,
      ...context.requestOptions,
    };

    if (!hasStructuredOutputs && this.aiToolSystem) {
      const tools = this.aiToolSystem.getToolsForModel();
      if (tools.length > 0) {
        payload.tools = tools;
      }
    }

    return payload;
  }

  /**
   * Convert OpenRouter tool definitions into the node-llama-cpp function definition shape.
   * Reuses the same frontend tool metadata so cloud and local stay aligned.
   * @param {string} modelId - Model identifier
   * @returns {Array<Object>} Array of node-llama-cpp function definitions
   */
  _getNodeLlamaCppFunctions(modelId) {
    if (!this.aiToolSystem) {
      return [];
    }

    const openRouterTools = this.aiToolSystem.getToolsForModel(modelId);
    if (!Array.isArray(openRouterTools) || openRouterTools.length === 0) {
      return [];
    }

    return openRouterTools
      .map((toolDef) => {
        const functionDef = toolDef?.function;
        if (!functionDef?.name) {
          return null;
        }

        return {
          name: functionDef.name,
          description: functionDef.description || `Call ${functionDef.name}`,
          params: functionDef.parameters || { type: "object", properties: {} },
        };
      })
      .filter(Boolean);
  }

  /**
   * Build the node-llama-cpp request shape used by the desktop backend.
   * @param {string} modelId - Model identifier
   * @param {Object} context - Inference context
   * @returns {{ prompt: string, history: Array<Object>, options: Object }}
   */
  _buildLocalLlamaCppRequest(modelId, context) {
    const chatFormat = this.chat.toNodeLlamaCppFormat();

    const requestOptions = context.requestOptions || {};
    // TODO (multimodal parity):
    // Local mode currently relies on the Chat converter’s best-effort text serialization.
    // Next step is to incorporate model capability awareness so we don’t explode multimodal
    // inputs into massive text blobs for local models that could accept structured inputs.
    const responseFormat = requestOptions.response_format || null;
    const jsonSchema =
      responseFormat?.type === "json_schema"
        ? responseFormat?.json_schema?.schema ||
          responseFormat?.json_schema ||
          null
        : null;

    return {
      prompt: typeof chatFormat?.prompt === "string" ? chatFormat.prompt : "",
      history: Array.isArray(chatFormat?.history) ? chatFormat.history : [],
      options: {
        ...(chatFormat?.systemPrompt
          ? { systemPrompt: chatFormat.systemPrompt }
          : {}),
        ...(jsonSchema ? { jsonSchema } : {}),
        ...(requestOptions.temperature != null
          ? { temperature: requestOptions.temperature }
          : {}),
        ...(requestOptions.topP != null
          ? { topP: requestOptions.topP }
          : requestOptions.top_p != null
            ? { topP: requestOptions.top_p }
            : {}),
        ...(requestOptions.topK != null
          ? { topK: requestOptions.topK }
          : requestOptions.top_k != null
            ? { topK: requestOptions.top_k }
            : {}),
        ...(requestOptions.maxTokens != null
          ? { maxTokens: requestOptions.maxTokens }
          : requestOptions.max_tokens != null
            ? { maxTokens: requestOptions.max_tokens }
            : {}),
        ...(requestOptions.seed != null ? { seed: requestOptions.seed } : {}),
        ...(requestOptions.repeatPenalty != null
          ? { repeatPenalty: requestOptions.repeatPenalty }
          : requestOptions.repeat_penalty != null
            ? { repeatPenalty: requestOptions.repeat_penalty }
            : {}),
        ...(requestOptions.responsePrefix
          ? { responsePrefix: requestOptions.responsePrefix }
          : {}),
        functions: this._getNodeLlamaCppFunctions(modelId),
      },
    };
  }

  /**
   * Remove active desktop event listeners for a local inference session.
   * @param {Object} context - Inference context
   */
  _cleanupLocalListeners(context) {
    if (!context || !Array.isArray(context.localCleanupFns)) {
      return;
    }

    for (const cleanup of context.localCleanupFns) {
      try {
        if (typeof cleanup === "function") {
          cleanup();
        }
      } catch (_) {
        // Ignore listener cleanup failures.
      }
    }

    context.localCleanupFns = [];
  }

  /**
   * Create a new assistant response message for the current context.
   * Local mode reuses this when one generation spans multiple tool boundaries.
   * @param {Object} context - Inference context
   * @param {Object} [options]
   * @param {boolean} [options.emitPrepared=true] - Emit onInferencePrepared for this message
   * @returns {string} Message ID
   */
  _createAssistantResponseMessage(context, options = {}) {
    const { emitPrepared = true } = options;

    context.segments = new Map();
    context.currentSegmentId = null;
    context.pendingToolCalls = new Map();
    context.completedToolCalls = new Map();
    context.streamingToolCalls = new Map();

    const aiMessage = {
      role: "assistant",
      content: null,
      segments: [],
      timestamp: Date.now(),
      usage: null,
    };

    const messageId = this.chat.addMessage(aiMessage);
    context.messageId = messageId;
    context.lastAssistantMessageId = messageId;

    if (!context.rootResponseMessageId) {
      context.rootResponseMessageId = messageId;
    }

    this.onChatModified("prepareInference", { messageId });

    if (emitPrepared) {
      this._emit("onInferencePrepared", messageId);
    }

    return messageId;
  }

  /**
   * Ensure the context has an active assistant message ready to receive segments.
   * @param {Object} context - Inference context
   * @param {Object} [options]
   * @param {boolean} [options.emitPrepared=false] - Emit onInferencePrepared for lazily created messages
   * @returns {string} Message ID
   */
  _ensureAssistantResponseMessage(context, options = {}) {
    if (context?.messageId && this.chat.getMessage(context.messageId)) {
      return context.messageId;
    }

    return this._createAssistantResponseMessage(context, {
      emitPrepared: options.emitPrepared ?? false,
    });
  }

  /**
   * Adopt the first local runId seen and ignore unrelated desktop stream traffic afterwards.
   * @param {Object} payload - Desktop stream or tool payload
   * @param {Object} context - Inference context
   * @returns {boolean} True if the payload belongs to this local run
   */
  _matchesLocalRun(payload, context) {
    const runId = typeof payload?.runId === "string" ? payload.runId : null;
    if (!runId) {
      return true;
    }

    if (!context.localRunId) {
      context.localRunId = runId;
      return true;
    }

    return context.localRunId === runId;
  }

  /**
   * Create a stable OpenRouter-style tool call id for local mode.
   * @param {string} runId
   * @param {number} callIndex
   * @returns {string}
   */
  _getLocalToolCallId(runId, callIndex) {
    const safeRunId =
      typeof runId === "string" && runId.length > 0 ? runId : "run-unknown";
    const safeCallIndex =
      typeof callIndex === "number" && Number.isFinite(callIndex)
        ? callIndex
        : -1;
    return `${safeRunId}:${safeCallIndex}`;
  }

  /**
   * Serialize a local tool result into the string content stored on OpenRouter-style tool messages.
   * @param {*} value
   * @returns {string}
   */
  _serializeLocalToolResult(value) {
    if (typeof value === "string") {
      return value;
    }

    if (value == null) {
      return "null";
    }

    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  }

  /**
   * Create a short tool result summary for backend/local UI plumbing.
   * @param {*} value
   * @returns {string}
   */
  _summarizeLocalToolResult(value) {
    if (typeof value === "string") {
      return value;
    }

    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  }

  /**
   * Map node-llama-cpp segment labels to the Revolt segment type that should receive text.
   * Honors {@link context.localPendingSegmentType} for in-flight named segments whose
   * `segmentType` label is temporarily missing (Core: regionSegmentType ?? pendingSegmentType).
   * @param {string} segmentTypeRaw
   * @param {boolean} isNamedSegment
   * @param {"reasoning"|"text"|null} pendingSegmentType
   * @returns {"reasoning"|"text"}
   */
  _resolveLocalOpenSegmentType(
    segmentTypeRaw,
    isNamedSegment,
    pendingSegmentType
  ) {
    if (!isNamedSegment) {
      return "text";
    }

    if (/^thought$|^reasoning$|^thinking$/i.test(segmentTypeRaw)) {
      return "reasoning";
    }

    if (segmentTypeRaw === "comment") {
      return "text";
    }

    if (pendingSegmentType) {
      return pendingSegmentType;
    }

    return "text";
  }

  /**
   * Interpret node-llama-cpp stream chunks into Revolt text/reasoning segments.
   * Mirrors Revolt Core `applyResponseChunk` segment boundary handling so thinking
   * bubbles open on `segmentStart` even when the first chunk has no visible text.
   * @param {Object} payload - Desktop generate-stream payload
   * @param {Object} context - Inference context
   */
  _handleLocalResponseChunk(payload, context) {
    const text = typeof payload?.text === "string" ? payload.text : "";

    // nlcp may emit literal empty-string boundary chunks; whitespace-only deltas are kept.
    if (text === "" && !payload?.isSegmentStart && !payload?.isSegmentEnd) {
      return;
    }

    const segmentTypeRaw =
      typeof payload?.segmentType === "string" ? payload.segmentType : "";
    const isNamedSegment = payload?.chunkType === "segment";
    const chunkKind = isNamedSegment ? "segment" : "text";

    if (context.localPendingSegmentType === undefined) {
      context.localPendingSegmentType = null;
    }

    const pendingSegmentType = context.localPendingSegmentType;
    const segmentTypeToOpen = this._resolveLocalOpenSegmentType(
      segmentTypeRaw,
      isNamedSegment,
      pendingSegmentType
    );
    const isReasoningSegment = segmentTypeToOpen === "reasoning";

    const isSegmentStart = !!payload?.isSegmentStart;
    const isSegmentEnd = !!payload?.isSegmentEnd;
    const hasText = text.length > 0;

    this._ensureAssistantResponseMessage(context, { emitPrepared: false });

    // Parity with cloud: remove the "generating" bubble on the first incoming chunk.
    if (context.isInitialConnectionPhase) {
      context.isInitialConnectionPhase = false;
      this._emit("onInitialConnectionPhaseEnd", context.messageId);
    }

    const getCurrentSegmentType = () => {
      if (!context.currentSegmentId) {
        return null;
      }
      return context.segments.get(context.currentSegmentId)?.type || null;
    };

    const openLocalSegment = (segmentType) => {
      if (
        context.currentSegmentId &&
        getCurrentSegmentType() === segmentType
      ) {
        context.localCurrentChunkKind = chunkKind;
        return;
      }

      this._endSegment(context);
      this._beginSegment(context, segmentType);
      context.localCurrentChunkKind = chunkKind;
    };

    if (isNamedSegment && isSegmentEnd) {
      if (hasText) {
        const endType = this._resolveLocalOpenSegmentType(
          segmentTypeRaw,
          isNamedSegment,
          pendingSegmentType
        );
        if (!context.currentSegmentId || getCurrentSegmentType() !== endType) {
          openLocalSegment(endType);
        }
        this._updateSegment(context, text);
      }

      this._endSegment(context);
      context.localCurrentChunkKind = null;
      context.localPendingSegmentType = null;
      return;
    }

    if (isNamedSegment && isSegmentStart && !hasText && !isSegmentEnd) {
      context.localPendingSegmentType = this._resolveLocalOpenSegmentType(
        segmentTypeRaw,
        true,
        null
      );
      return;
    }

    if (!hasText) {
      return;
    }

    if (!isNamedSegment) {
      const inReasoningStream =
        getCurrentSegmentType() === "reasoning" ||
        pendingSegmentType === "reasoning";

      if (inReasoningStream) {
        if (!context.currentSegmentId || getCurrentSegmentType() !== "reasoning") {
          openLocalSegment("reasoning");
        }
        this._updateSegment(context, text);
        context.localCurrentChunkKind = chunkKind;
        return;
      }

      const openingMainAnswer =
        !context.currentSegmentId || getCurrentSegmentType() !== "text";
      const mainAnswerDelta = openingMainAnswer ? text.trimStart() : text;
      if (!mainAnswerDelta.length) {
        return;
      }

      if (getCurrentSegmentType() === "reasoning") {
        this._endSegment(context);
      }

      if (
        !context.currentSegmentId ||
        context.localCurrentChunkKind !== chunkKind ||
        getCurrentSegmentType() !== "text"
      ) {
        openLocalSegment("text");
      }

      this._updateSegment(context, mainAnswerDelta);
      context.localPendingSegmentType = null;

      if (isSegmentEnd) {
        this._endSegment(context);
        context.localCurrentChunkKind = null;
      }
      return;
    }

    if (
      isSegmentStart &&
      context.currentSegmentId &&
      getCurrentSegmentType() !== segmentTypeToOpen
    ) {
      this._endSegment(context);
    }

    if (
      !context.currentSegmentId ||
      getCurrentSegmentType() !== segmentTypeToOpen
    ) {
      openLocalSegment(segmentTypeToOpen);
    }

    this._updateSegment(context, text);

    if (isReasoningSegment) {
      context.localPendingSegmentType = "reasoning";
    } else if (segmentTypeRaw === "comment") {
      context.localPendingSegmentType = null;
    }

    if (isSegmentEnd) {
      this._endSegment(context);
      context.localCurrentChunkKind = null;
      context.localPendingSegmentType = null;
    }
  }

  /**
   * Track local tool argument streaming so the frontend can persist the eventual tool call cleanly.
   * @param {Object} payload - Desktop function-call-params-chunk payload
   * @param {Object} context - Inference context
   */
  _handleLocalFunctionCallParamsChunk(payload, context) {
    this._ensureAssistantResponseMessage(context, { emitPrepared: false });

    // Parity with cloud: if the first signal for a response is tool-call params,
    // we still want to drop the generating bubble immediately.
    if (context.isInitialConnectionPhase) {
      context.isInitialConnectionPhase = false;
      this._emit("onInitialConnectionPhaseEnd", context.messageId);
    }

    const callIndex =
      typeof payload?.callIndex === "number" ? payload.callIndex : -1;
    const functionName =
      typeof payload?.functionName === "string" ? payload.functionName : "unknown";
    const resolvedToolName =
      this.aiToolSystem?.resolveRegisteredToolName?.(functionName) || functionName;
    const paramsChunk =
      typeof payload?.paramsChunk === "string" ? payload.paramsChunk : "";
    const runId =
      typeof payload?.runId === "string" ? payload.runId : context.localRunId;

    let accumulated = context.streamingToolCalls.get(callIndex);
    if (!accumulated) {
      accumulated = {
        index: callIndex,
        id: this._getLocalToolCallId(runId, callIndex),
        function: {
          name: null,
          arguments: "",
        },
        timestamp: Date.now(),
        status: "accumulating",
      };
      context.streamingToolCalls.set(callIndex, accumulated);
    }

    if (resolvedToolName && !accumulated.function.name) {
      accumulated.function.name = resolvedToolName;
      this._emit("onToolCallFunctionKnown", context.messageId, {
        toolCallId: accumulated.id,
        functionName: resolvedToolName,
        index: callIndex,
        status: "accumulating_arguments",
        timestamp: accumulated.timestamp,
      });
    }

    if (paramsChunk.length > 0) {
      accumulated.function.arguments += paramsChunk;

      // Forward per-argument-chunk UI streaming event (cloud + local parity)
      if (accumulated.id) {
        this._emit("onToolCallParamsChunk", context.messageId, {
          toolCallId: accumulated.id,
          functionName: accumulated.function.name || resolvedToolName,
          index: callIndex,
          paramsChunk,
          paramsTextSoFar: accumulated.function.arguments,
          done: false,
        });
      }
    }
  }

  /**
   * Persist a local tool call onto the current assistant message in OpenRouter-compatible shape.
   * @param {Object} context - Inference context
   * @param {Object} toolCallData - { toolCallId, functionName, arguments }
   */
  _appendLocalToolCallToTranscript(context, toolCallData) {
    this._ensureAssistantResponseMessage(context, { emitPrepared: false });
    this._endSegment(context);

    const message = this.chat.getMessage(context.messageId);
    const existingToolCalls = Array.isArray(message?.data?.tool_calls)
      ? [...message.data.tool_calls]
      : [];

    const toolCall = {
      id: toolCallData.toolCallId,
      type: "function",
      function: {
        name: toolCallData.functionName,
        arguments: JSON.stringify(toolCallData.arguments || {}),
      },
    };

    existingToolCalls.push(toolCall);

    this.chat.updateMessage(context.messageId, {
      tool_calls: existingToolCalls,
    });

    const pendingToolCall = {
      id: toolCallData.toolCallId,
      function: {
        name: toolCallData.functionName,
        arguments: toolCallData.arguments || {},
      },
      timestamp: Date.now(),
      status: "executing",
    };

    context.pendingToolCalls.set(toolCallData.toolCallId, pendingToolCall);

    this._beginSegment(context, "tool_call", {
      toolCallId: toolCallData.toolCallId,
      functionName: toolCallData.functionName,
      arguments: toolCallData.arguments || {},
      blocking: true,
    });
    this._emit("onToolCallStarted", context.messageId, pendingToolCall);
    this._endSegment(context);
    this.onChatModified("localToolCall", {
      messageId: context.messageId,
      toolCallId: toolCallData.toolCallId,
      functionName: toolCallData.functionName,
    });
  }

  /**
   * Persist a local tool result as an OpenRouter-compatible tool message.
   * @param {Object} context - Inference context
   * @param {Object} resultData - { toolCallId, result, success }
   */
  _appendLocalToolResultToTranscript(context, resultData) {
    const content = this._serializeLocalToolResult(resultData.result);
    const toolResult = {
      tool_call_id: resultData.toolCallId,
      content,
      success: resultData.success !== false,
    };

    this.chat.addMessage({
      role: "tool",
      tool_call_id: resultData.toolCallId,
      content,
    });

    context.completedToolCalls.set(resultData.toolCallId, toolResult);
    const toolCall = context.pendingToolCalls.get(resultData.toolCallId) || {
      id: resultData.toolCallId,
      function: { name: "unknown", arguments: {} },
    };

    toolCall.status = resultData.success !== false ? "completed" : "failed";
    toolCall.result = resultData.result;

    this._emit("onToolCallCompleted", context.messageId, toolCall, resultData.result);
    this.onChatModified("toolResults", { toolResultsCount: 1 });
  }

  /**
   * Execute a local tool request dispatched from the desktop backend and return the result to node-llama-cpp.
   * @param {Object} payload - Desktop tool dispatch payload
   * @param {string} modelId - Model identifier
   * @param {Object} context - Inference context
   */
  async _handleLocalToolDispatch(payload, modelId, context) {
    const runId =
      typeof payload?.runId === "string" ? payload.runId : context.localRunId;
    const callIndex =
      typeof payload?.callIndex === "number" ? payload.callIndex : -1;
    const toolCallId = this._getLocalToolCallId(runId, callIndex);
    const rawFunctionName =
      typeof payload?.functionName === "string" ? payload.functionName : "unknown";
    const functionName =
      this.aiToolSystem?.resolveRegisteredToolName?.(rawFunctionName) ||
      rawFunctionName;
    const params =
      payload?.params && typeof payload.params === "object" ? payload.params : {};
    const executionKey = toolCallId;
    const api = typeof window !== "undefined" ? window.llamaCppAPI : null;

    if (!api?.toolExecutionComplete) {
      throw new Error("Desktop local tool execution API is not available");
    }

    if (context.localActiveToolExecutions?.has(executionKey)) {
      return;
    }

    if (!context.localActiveToolExecutions) {
      context.localActiveToolExecutions = new Set();
    }

    context.localActiveToolExecutions.add(executionKey);

    const prevActiveToolContext = this.activeToolContext;
    this.activeToolContext = context;

    try {
      this._appendLocalToolCallToTranscript(context, {
        toolCallId,
        functionName,
        arguments: params,
      });
      context.streamingToolCalls.delete(callIndex);

      const result = await this.aiToolSystem.executeTool(
        functionName,
        modelId,
        params
      );

      const { modelFacing, haltAfterAssistantToolBatch } =
        this._partitionToolExecuteResult(result);
      context.haltInferenceAfterAssistantToolBatch =
        context.haltInferenceAfterAssistantToolBatch ||
        haltAfterAssistantToolBatch;

      this._appendLocalToolResultToTranscript(context, {
        toolCallId,
        result: modelFacing,
        success: true,
      });

      await api.toolExecutionComplete(runId, callIndex, {
        ok: true,
        result: modelFacing,
        summary: this._summarizeLocalToolResult(modelFacing),
        haltInferenceAfterAssistantToolBatch:
          !!context.haltInferenceAfterAssistantToolBatch,
      });
    } catch (error) {
      const errorResult = {
        success: false,
        error: error?.message || String(error),
      };

      this._appendLocalToolResultToTranscript(context, {
        toolCallId,
        result: errorResult,
        success: false,
      });

      await api.toolExecutionComplete(runId, callIndex, {
        ok: false,
        error: errorResult.error,
        summary: errorResult.error,
        result: errorResult,
        haltInferenceAfterAssistantToolBatch:
          !!context.haltInferenceAfterAssistantToolBatch,
      });
    } finally {
      this.activeToolContext = prevActiveToolContext;
      context.localActiveToolExecutions.delete(executionKey);
      context.messageId = null;
      context.currentSegmentId = null;
      context.localCurrentChunkKind = null;
      context.localPendingSegmentType = null;
      context.pendingToolCalls.clear();
      context.completedToolCalls.clear();
      context.streamingToolCalls.clear();
    }
  }

  /**
   * Process desktop local stream events.
   * Local mode never uses the OpenRouter tool recursion boundary.
   * @param {Object} payload - Desktop generate-stream payload
   * @param {Object} context - Inference context
   */
  _handleLocalGenerateStreamEvent(payload, context) {
    switch (payload?.type) {
      case "response-chunk":
        this._handleLocalResponseChunk(payload, context);
        break;

      case "function-call-params-chunk":
        this._handleLocalFunctionCallParamsChunk(payload, context);
        break;

      case "tool-call-executing": {
        const toolCallId = this._getLocalToolCallId(
          payload?.runId,
          payload?.callIndex
        );
        const toolCall = context.pendingToolCalls.get(toolCallId);
        if (toolCall) {
          toolCall.status = "executing";
        }
        break;
      }

      case "tool-call-complete":
        // Transcript persistence happens when the frontend executes the tool and returns
        // the structured result to the backend. This stream event is informational only.
        break;

      case "done":
        this._endSegment(context);
        break;

      case "error":
        throw new Error(payload?.error || "Local inference failed");

      default:
        break;
    }
  }

  /**
   * Local mode inference using the Revolt Desktop llamaCpp API directly.
   * This keeps generation in a single backend run while persisting OpenRouter-style transcript turns.
   * @param {string} modelId - Local model identifier
   * @param {Object} context - Inference context
   * @returns {Promise<Object>} Stream-like result object
   */
  async _performLocalInference(modelId, context) {
    const api =
      typeof window !== "undefined" ? window.llamaCppAPI || null : null;
    if (!api?.generate || !api?.onGenerateStream) {
      throw new Error(
        "Local inference requires the Revolt Desktop llamaCpp API"
      );
    }

    const localRequest = this._buildLocalLlamaCppRequest(modelId, context);

    if (typeof api.setChatHistory === "function") {
      const historyResult = await api.setChatHistory(localRequest.history || []);
      if (historyResult?.success === false) {
        throw new Error(historyResult.error || "Failed to sync local chat history");
      }
    }

    context.localRunId = null;
    context.localCleanupFns = [];
    context.localActiveToolExecutions = new Set();
    context.localCurrentChunkKind = null;
    context.localPendingSegmentType = null;
    context.haltInferenceAfterAssistantToolBatch = false;

    let streamError = null;

    const subscribeAsync = (register, handler) => {
      if (typeof register !== "function") {
        return;
      }

      const cleanup = register((payload) => {
        Promise.resolve()
          .then(async () => {
            if (!this._matchesLocalRun(payload, context)) {
              return;
            }
            await handler(payload);
          })
          .catch((error) => {
            if (!streamError) {
              streamError = error;
            }
          });
      });

      if (typeof cleanup === "function") {
        context.localCleanupFns.push(cleanup);
      }
    };

    subscribeAsync(api.onGenerateStream.bind(api), async (payload) => {
      this._handleLocalGenerateStreamEvent(payload, context);
    });

    if (
      typeof api.onToolCallDispatch === "function" &&
      typeof api.toolExecutionComplete === "function"
    ) {
      subscribeAsync(api.onToolCallDispatch.bind(api), async (payload) => {
        await this._handleLocalToolDispatch(payload, modelId, context);
      });
    }

    try {
      const result = await api.generate(localRequest.prompt || "", localRequest.options);
      await Promise.resolve();

      if (streamError) {
        throw streamError;
      }

      if (result?.success === false) {
        throw new Error(result.error || "Local inference failed");
      }

      if (!context.messageId && context.lastAssistantMessageId) {
        context.messageId = context.lastAssistantMessageId;
      }

      return {
        hasToolCalls: false,
        finishReason: context.cancelled ? "cancelled" : "stop",
        usage: null,
      };
    } finally {
      this._cleanupLocalListeners(context);
    }
  }

  /**
   * Prepare request body for OpenRouter API (cloud). Uses shared _prepareInferencePayload + cloud-only fields.
   * @param {string} modelId - Model identifier
   * @param {Object} context - Context containing additional data
   * @param {Object} context.requestOptions - Additional request options including reasoning
   * @returns {Promise<Object>} Request body for fetch
   */
  async prepareRequest(modelId, context = {}) {
    const payload = await this._prepareInferencePayload(modelId, context);
    return {
      ...payload,
      instance: this.getInstanceId(), // AI tool instance identifier for backend permissions (cloud only)
      instanceContext:
        context.instanceContext &&
        typeof context.instanceContext === "object" &&
        !Array.isArray(context.instanceContext)
          ? context.instanceContext
          : null,
    };
  }

  /**
   * Make streaming request to the cloud OpenRouter path.
   * Local mode uses a dedicated pipeline and does not use this method.
   * @param {string} modelId - Model identifier
   * @param {Object} context - Inference context
   * @returns {Promise<Response>} Fetch response
   */
  async _makeStreamingRequest(modelId, context) {
    return this._makeCloudStreamingRequest(modelId, context);
  }

  /**
   * Cloud mode: streaming request to Revolt site backend (OpenRouter manager).
   * Uses fetch to /api/openrouter/chat/completions.
   * @param {string} modelId - Model identifier (OpenRouter model id)
   * @param {Object} context - Inference context
   * @returns {Promise<Response>} Fetch response
   */
  async _makeCloudStreamingRequest(modelId, context) {
    const requestBody = await this.prepareRequest(modelId, context);

    // Generate request ID for cancellation tracking
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    context.requestId = requestId;
    context.cancelled = false; // Track if user cancelled

    // Add requestId to the request body
    const requestBodyWithId = {
      ...requestBody,
      requestId,
      inferenceSessionId: context.inferenceSessionId || null,
    };

    const response = await fetch("/api/openrouter/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBodyWithId),
      signal: context.cancelController.signal,
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `API request failed: ${response.status} ${response.statusText}`;

      // Check for common proxy/gateway errors that return HTML instead of JSON
      // Note: 503 is excluded because backend uses it for user-friendly service errors
      if (response.status === 504 || response.status === 502) {
        errorMessage = "AI inference service is temporarily unavailable due to network issues. Please try again in a moment.";
        logger.warn(
          {
            tags: "ai-inference|gateway-error",
            color1: "yellow"
          },
          `Gateway error detected: ${response.status} ${response.statusText}`
        );
        throw new Error(errorMessage);
      }

      // Pre-stream error decoding: normalize ANYTHING the backend returned
      // (new envelope or legacy SERVICE_TEMPORARILY_UNAVAILABLE / INSUFFICIENT_CREDITS
      // shape) through `normalizeInferenceError`. A single code path, a single
      // resulting Error.revolt envelope -- see assessment §7.Q3.
      let parsedError = null;
      try {
        const errorData = await response.text();
        if (errorData) {
          try {
            parsedError = JSON.parse(errorData);
          } catch (_parseErr) {
            logger.warn(
              { tags: "ai-inference|error-parsing", color1: "yellow" },
              "Failed to parse error response as JSON:",
              { errorData: errorData.substring(0, 200) + (errorData.length > 200 ? "..." : "") }
            );
          }
        }
      } catch (readError) {
        logger.warn(
          { tags: "ai-inference|error|read-body", color1: "yellow" },
          "Could not read error response body:",
          readError
        );
      }

      const normalized = normalizeInferenceError(
        parsedError || { error: errorMessage }
      );

      // Report SERVICE_TEMPORARILY_UNAVAILABLE-class errors as a service
      // incident (preserves existing analytics behavior).
      if (
        normalized.revolt.errorCode ===
          REVOLT_ERROR_CODES.SERVICE_TEMPORARILY_UNAVAILABLE ||
        normalized.revolt.incidentType
      ) {
        const activeContext = this.activeContexts.values().next().value;
        const incidentType =
          normalized.revolt.incidentType ||
          this._mapHttpStatusToIncidentType(
            normalized.provider?.httpStatus || response.status
          );
        this._reportServiceIncident(incidentType, {
          userId: activeContext?.userId || null,
          details: {
            endpoint: "/api/openrouter/chat/completions",
            httpStatus: normalized.provider?.httpStatus || response.status,
            revoltErrorCode: normalized.revolt.errorCode,
            originalError: parsedError?.originalError || parsedError?.error || null,
            originalErrorDetails: parsedError?.originalErrorDetails || null,
            backendStatus: response.status,
            timestamp: new Date().toISOString(),
            modelId: activeContext?.modelId || null,
          },
          metadata: {
            userAgent:
              typeof navigator !== "undefined" ? navigator.userAgent : null,
            url: typeof window !== "undefined" ? window.location.href : null,
            timestamp: Date.now(),
            requestId: activeContext?.requestId || null,
          },
        }).catch((reportError) => {
          logger.warn(
            { tags: "ai-inference|incident-report|error", color1: "yellow" },
            "Failed to report service incident:",
            reportError
          );
        });
      }

      throw errorFromEnvelope(normalized);
    }

    return response;
  }

  /**
   * Process streaming response from OpenRouter
   * @param {Response} response - Streaming response
   * @param {Object} context - Inference context
   * @returns {Promise<Object>} Final result with usage data
   */
  async _processStreamingResponse(response, context) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let buffer = "";

    // Initialize streaming status at the start of processing
    context.streamingStatus = null;
    context.isInitialConnectionPhase = true;

    try {
      while (true) {
        // Continue reading until connection is aborted or naturally ends
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();

            if (data === "[DONE]") {
              break;
            }

            try {
              const chunk = JSON.parse(data);
              // Set status to "data" when we receive actual AI response chunks
              context.streamingStatus = "data";

              // Check if we're transitioning from initial connection phase to active streaming
              const wasInitialPhase = context.isInitialConnectionPhase;
              // Mark that we've moved past the initial connection phase
              context.isInitialConnectionPhase = false;

              // Emit event if this is the transition from initial phase to active streaming
              if (wasInitialPhase) {
                this._emit("onInitialConnectionPhaseEnd", context.messageId);
              }
              const result = await this._handleChunkReceived(chunk, context);

              if (result.hasToolCalls) {
                // Stop processing chunks when tool calls are detected
                return { hasToolCalls: true, usage: result.usage };
              }
            } catch (error) {
              logger.error(
                {
                  tags: "ai-inference|chunk-parse",
                  color1: "red",
                },
                "Failed to parse chunk:",
                error,
                "Data:",
                data
              );
            }
          } else if (line.trim() === ":") {
            // Stream start indicator
            context.streamingStatus = "start";
            logger.log(
              { tags: "ai-inference|stream|start", color1: "green" },
              "Stream start detected"
            );
            this._emit("onStreamStart", context.messageId);
          } else if (line.includes("OPENROUTER PROCESSING")) {
            // Stream idle/processing indicator
            context.streamingStatus = "idle";
            logger.log(
              { tags: "ai-inference|stream|idle", color1: "blue" },
              "OpenRouter processing status"
            );
            this._emit("onStreamIdle", context.messageId);
          } else if (line.trim() !== "") {
            // Log unexpected non-empty lines for debugging
            logger.warn(
              { tags: "ai-inference|stream|unknown", color1: "yellow" },
              "Unknown streaming line format:",
              line
            );
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return { hasToolCalls: false, usage: null };
  }

  /**
   * Handle revolt chunks (custom backend chunks)
   * @param {Object} parsedChunk - Parsed revolt chunk
   * @param {Object} context - Inference context
   * @returns {Promise<Object>} Processing result
   */
  async _handleRevoltChunk(parsedChunk, context) {
    switch (parsedChunk.type) {
      case "tool_call_approval":
        // Proceed with tool calls if approved and we have pending tool calls
        if (parsedChunk.approved && context.pendingToolCalls.size > 0) {
          return { hasToolCalls: true, usage: null };
        }
        break;

      case "completion_finished":
        // Generic completion signal - could be used for other logic in the future
        logger.log(
          {
            tags: "ai-inference|debug|completion-finished",
            color1: "blue",
          },
          "[ON INFERENCE FINISHED DEBUG] Revolt completion_finished received",
          {
            messageId: context.messageId,
            finishReason: parsedChunk.finishReason,
            approved: parsedChunk.approved,
            hasEmittedInferenceFinished: context.hasEmittedInferenceFinished,
          }
        );
        if (!context.hasEmittedInferenceFinished) {
          this._handleCompletion(context, parsedChunk.finishReason || "stop");
        }
        break;

      case "cancellation_acknowledged":
        // User cancellation acknowledged - now we can abort the connection
        logger.log(
          { tags: "ai-inference|cancellation", color1: "blue" },
          "Cancellation acknowledged by backend, aborting connection",
          { cancelled: parsedChunk.cancelled, hasUsage: parsedChunk.hasUsage }
        );

        this._handleCompletion(context, "cancelled");

        // Abort the connection now that we've received acknowledgement
        if (context.cancelController) {
          context.cancelController.abort();
        }
        break;

      case "inference_error":
        // New unified error sidecar (see OpenRouterManager inference envelope in openrouter-manager.js). Covers
        // mid-stream `data.error`, usage_missing, stream_incomplete, etc.
        this._handleInferenceErrorChunk(parsedChunk, context);
        break;

      case "abnormal_termination":
        // Legacy path: older backends emit abnormal_termination without the new
        // envelope. Normalize and route through the same handler as inference_error
        // so the UI has a single error funnel.
        logger.warn(
          {
            tags: "ai-inference|abnormal-termination|legacy",
            color1: "yellow",
          },
          "abnormal_termination chunk received without envelope; normalizing via legacy path"
        );
        this._handleInferenceErrorChunk(parsedChunk, context);
        break;

      default:
        logger.warn(
          {
            tags: "ai-inference|revolt-chunk|warning",
            color1: "yellow",
          },
          "Unknown revolt chunk type:",
          parsedChunk.type
        );
        break;
    }

    return { hasToolCalls: false, usage: null };
  }

  /**
   * Handle a received chunk
   * @param {Object} chunk - Raw chunk from API
   * @param {Object} context - Inference context
   * @returns {Promise<Object>} Processing result
   */
  async _handleChunkReceived(chunk, context) {
    // Handle revolt chunks directly (our custom protocol - bypass OpenRouter parsing)
    if (chunk.__revolt) {
      return this._handleRevoltChunk(chunk, context);
    }

    // Defensive: if a raw OpenRouter chunk still carries a top-level `error`
    // (e.g. talking to an older backend that doesn't inject our `inference_error`
    // sidecar), funnel it through the same error path so it doesn't silently hang.
    if (chunk && typeof chunk === "object" && chunk.error) {
      logger.warn(
        { tags: "ai-inference|mid-stream-error|legacy", color1: "yellow" },
        "Mid-stream OpenRouter error received without Revolt sidecar; normalizing:",
        chunk.error
      );
      this._handleInferenceErrorChunk(
        {
          type: "inference_error",
          envelope: {
            revolt: {
              errorCode: REVOLT_ERROR_CODES.PROVIDER_ERROR,
              phase: "mid_stream",
              userMessage:
                chunk.error?.message ||
                "The AI provider reported an error mid-stream. Please try again.",
              retryable: true,
              incidentType: "mid_stream_error",
            },
            provider: {
              name: "openrouter",
              httpStatus: Number(chunk.error?.code) || null,
              error: chunk.error,
            },
          },
        },
        context
      );
      return { hasToolCalls: false, usage: null };
    }

    // Parse and handle normal OpenRouter chunks
    const parsedChunk = this._parseChunk(chunk);

    // Always accumulate usage data (needed for billing)
    if (parsedChunk.usage) {
      this._accumulateUsage(context, parsedChunk.usage);
    }

    // Skip UI updates if user cancelled, but still process data
    if (!context.cancelled) {
      // Accumulate tool calls from streaming chunks (don't execute yet)
      this._accumulateStreamingToolCalls(context, parsedChunk.toolCalls);

      // Process content for segments
      this._processContentForSegments(context, parsedChunk);
    }

    // Check for completion
    if (parsedChunk.finishReason) {
      // End any current segment before finishing
      this._endSegment(context);

      // Special handling for tool_calls finish reason
      if (parsedChunk.finishReason === "tool_calls") {
        // Finalize accumulated streaming tool calls (convert to pending but don't execute yet)
        this._finalizeStreamingToolCalls(context);

        // Return finish reason to stop streaming - revolt chunk will trigger execution later
        return {
          hasToolCalls: false,
          finishReason: parsedChunk.finishReason,
          usage: parsedChunk.usage,
          toolCallsReady: true, // Signal that tool calls are ready for approval
        };
      }

      // Regular completion
      return {
        hasToolCalls: false,
        finishReason: parsedChunk.finishReason,
        usage: parsedChunk.usage,
      };
    }

    return { hasToolCalls: false, usage: parsedChunk.usage };
  }

  /**
   * Map HTTP status code to appropriate incident type for SERVICE_TEMPORARILY_UNAVAILABLE errors
   * @param {number} statusCode - HTTP status code from the original API error
   * @returns {string} Incident type
   */
  _mapHttpStatusToIncidentType(statusCode) {
    // Billing and quota related errors
    if (statusCode === 402) return 'payment_required'; // Payment Required
    if (statusCode === 429) return 'quota_exceeded';   // Too Many Requests (rate limit/quota)

    // Network and infrastructure errors
    if (statusCode === 502) return 'network_error';    // Bad Gateway
    if (statusCode === 503) return 'network_error';    // Service Unavailable
    if (statusCode === 504) return 'network_error';    // Gateway Timeout

    // Timeout errors
    if (statusCode === 408) return 'api_timeout';      // Request Timeout

    // Model/service availability
    if (statusCode === 404) return 'model_unavailable'; // Not Found (model doesn't exist)

    // Default fallback for unknown status codes
    return 'network_error';
  }

  /**
   * Generate contextual error message based on incident type and original error details
   * @param {string} incidentType - The incident type (quota_exceeded, etc.)
   * @param {Object} parsedError - The parsed error object from backend
   * @param {Object} activeContext - The active inference context
   * @returns {string} Contextual error message
   */
  _getContextualErrorMessage(incidentType, parsedError, activeContext) {
    const originalError = parsedError.originalError || '';
    const originalErrorDetails = parsedError.originalErrorDetails || {};
    const metadataRaw = originalErrorDetails.metadata?.raw || '';
    const modelId = activeContext?.modelId || 'selected';

    // Check for specific error patterns in both the basic error and detailed metadata
    const checkErrorContent = (content) => content.toLowerCase();

    // Use metadata.raw if available (more detailed), otherwise fall back to originalError
    const errorContent = metadataRaw || originalError;

    // Check for specific error patterns and provide contextual messages
    switch (incidentType) {
      case 'quota_exceeded':
        // Check if it's rate limiting upstream from the provider (in metadata.raw or original error)
        if (checkErrorContent(errorContent).includes('rate-limited upstream') ||
            checkErrorContent(errorContent).includes('rate limited') ||
            checkErrorContent(errorContent).includes('rate limit') ||
            checkErrorContent(errorContent).includes('temporarily rate-limited')) {
          return `The ${modelId} model is temporarily rate-limited by its provider. Please try a different model or wait a moment before retrying.`;
        }
        // Generic quota exceeded message (external service issue, not user plan)
        return `The AI service is currently experiencing quota limitations. This is not related to your account - please try a different model or wait a moment before retrying.`;

      case 'model_unavailable':
        return `The ${modelId} model is currently unavailable. Please try selecting a different model.`;

      case 'payment_required':
        return `The AI service is temporarily unavailable due to a billing synchronization issue. This is not related to your account - we've been notified and will address this shortly. Please try again in a moment.`;

      case 'openrouter_outage':
        return `OpenRouter (our AI provider) is currently experiencing an outage. Check [status.openrouter.ai]{https://status.openrouter.ai} for updates, or try again in a moment.`;

      case 'network_error':
        return `There are currently network connectivity issues. Please try again in a moment.`;

      case 'api_timeout':
        return `The request timed out. Please try again.`;

      default:
        // Fall back to the backend's generic user-friendly message
        return parsedError.error || "AI inference service is temporarily unavailable. This is not related to your account - we've been notified and will address this shortly. Hang tight!";
    }
  }

  /**
   * Report a service incident to the backend
   * @param {string} type - Incident type
   * @param {Object} context - Incident context
   * @returns {Promise<Object>} Response from incident reporting
   */
  async _reportServiceIncident(type, context = {}) {
    try {
      const response = await fetch("/api/incidents/report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: type,
          context: context,
        }),
        credentials: "include", // Include session cookies
      });

      if (!response.ok) {
        throw new Error(`Incident reporting failed: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      logger.error(
        {
          tags: "ai-inference|incident-report|error",
          color1: "red",
        },
        "Service incident reporting failed:",
        error
      );
      throw error;
    }
  }

  /**
   * Accumulate usage data in context
   * @param {Object} context - Inference context
   * @param {Object} usage - Usage data from chunk
   */
  _accumulateUsage(context, usage) {
    if (!usage) return;

    Object.keys(context.totalUsage).forEach((key) => {
      if (usage[key] !== undefined) {
        context.totalUsage[key] += usage[key];
      }
    });
  }

  // ==========================================
  // CORE INFERENCE METHOD
  // ==========================================

  /**
   * Perform inference with streaming and tool call support
   * @param {string} modelId - Model identifier (OpenRouter id for cloud; local model id for local mode)
   * @param {string|null} userId - User ID for frontend incident reporting and logging only.
   *                               NOT used for backend authentication or billing - backend securely
   *                               identifies user from session/token for billing purposes.
   * @param {Object} context - Shared context object (created on first call)
   * @param {Object} options - Additional options
   * @param {string} options.mode - Inference mode: "cloud" (default) uses Revolt site backend (OpenRouter);
   *                               "local" uses RevoltDesktop backend (NodeLlamaCpp via window.* API).
   * @param {Object} options.reasoning - Reasoning configuration (effort: "none"|"minimal"|"low"|"medium"|"high"|"xhigh", exclude: boolean)
   * @param {Object} options.requestOptions - Additional request options to pass to API
   * @returns {Promise<Object>} Context object with accumulated data
   */
  async infer(modelId, userId = null, context = null, options = {}) {
    // Create context on initial call
    const isInitialCall = !context;
    if (isInitialCall) {
      context = this._createContext(modelId, { userId, ...options });
    } else {
      // Preserve the same live context object across recursive sub-generations.
      // Cancellation, request tracking, segments, and tool state all rely on stable
      // object identity for the active inference chain.
      context = this._ensureContextDefaults(context, modelId, {
        userId,
        ...options,
      });
      context.recursionDepth++;
    }

    // Merge reasoning options into requestOptions if provided
    if (options.reasoning) {
      // Validate reasoning configuration
      this._validateReasoningConfig(options.reasoning);
      context.requestOptions = context.requestOptions || {};
      context.requestOptions.reasoning = options.reasoning;
    }

    // Merge any additional requestOptions
    if (options.requestOptions) {
      context.requestOptions = {
        ...context.requestOptions,
        ...options.requestOptions,
      };
    }

    try {
      // Phase 1: Prepare messages and make API call
      await this._prepareForInference(context);

      // Phase 2: Stream and process chunks
      const streamResult =
        context.inferenceMode === "local"
          ? await this._performLocalInference(modelId, context)
          : await this._performStreamingInference(modelId, context);

      // Phase 3: Handle tool calls (if any) with recursion
      if (streamResult.hasToolCalls && context.inferenceMode !== "local") {
        await this._handleToolCallsAndRecurse(modelId, context, userId);
      }

      // Phase 4: Handle completion
      else if (streamResult.finishReason) {
        logger.log(
          {
            tags: "ai-inference|debug|completion",
            color1: "blue",
          },
          "[ON INFERENCE FINISHED DEBUG] Completion detected in infer()",
          {
            messageId: context.messageId,
            finishReason: streamResult.finishReason,
            recursionDepth: context.recursionDepth,
          }
        );
        this._handleCompletion(context, streamResult.finishReason);
      }

      // Return context (recursive calls return but their return values are ignored)
      return context;
    } catch (error) {
      // Check if this is a user cancellation (AbortError)
      if (context?.cancelled) {
        // Local mode cancellation can surface as provider/backend errors rather than AbortError.
        // If we already marked the context cancelled, treat it as a graceful cancellation.
        this._handleCompletion(context, "cancelled");
        return context;
      }

      if (
        error.name === "AbortError" ||
        error.message?.includes("aborted") ||
        error.message?.includes("BodyStreamBuffer was aborted")
      ) {
        // Handle as successful cancellation - don't throw error
        this._handleCompletion(context, "cancelled");
        return context;
      } else {
        // Handle as actual error
        this._handleError(context, error);
        throw error;
      }
    } finally {
      // Clean up on final call only
      if (isInitialCall) {
        this._cleanupContext(context);
      }
    }
  }

  /**
   * Prepare chat messages for inference
   * @param {Object} context - Inference context
   */
  async _prepareForInference(context) {
    this._createAssistantResponseMessage(context, { emitPrepared: true });
  }

  /**
   * Perform streaming inference
   * @param {string} modelId - Model identifier
   * @param {Object} context - Inference context
   * @returns {Promise<Object>} Stream result
   */
  async _performStreamingInference(modelId, context) {
    const response = await this._makeStreamingRequest(modelId, context);
    const result = await this._processStreamingResponse(response, context);

    return result;
  }

  /**
   * Handle tool calls and recurse for continuation
   * @param {string} modelId - Model identifier
   * @param {Object} context - Inference context
   * @param {string|null} userId - User ID
   */
  async _handleToolCallsAndRecurse(modelId, context, userId) {
    // Fresh wave: tools may set this during execution (return payload or
    // `queueHaltInferenceAfterAssistantToolBatch()`); OR across the loop.
    context.haltInferenceAfterAssistantToolBatch = false;

    // Execute pending tool calls
    const toolResults = await this._executeToolCalls(context, modelId);

    // Add tool results as tool messages (continuation pattern)
    for (const result of toolResults) {
      this.chat.addMessage({
        role: "tool",
        tool_call_id: result.tool_call_id,
        content: result.content,
      });
    }
    this.onChatModified('toolResults', { toolResultsCount: toolResults.length });

    if (context.cancelled) {
      if (!context.hasEmittedInferenceFinished) {
        this._handleCompletion(context, "cancelled");
      }
      return;
    }

    if (context.haltInferenceAfterAssistantToolBatch) {
      context.haltInferenceAfterAssistantToolBatch = false;
      if (!context.hasEmittedInferenceFinished) {
        this._handleCompletion(context, "stop");
      }
      return;
    }

    // Recurse with the same context (tool results trigger new API call)
    // Note: We don't capture the return value from the recursive call
    await this.infer(modelId, userId, context, {});
  }

  /**
   * Handle completion scenarios
   * @param {Object} context - Inference context
   * @param {string} finishReason - Reason for completion
   */
  _handleCompletion(context, finishReason) {
    // End any remaining segments
    this._endSegment(context);

    // Update message with final data
    const message = this.chat.getMessage(context.messageId);
    if (message) {
      message.usage = { ...context.totalUsage };
      message.finishReason = finishReason;
      message.completedAt = Date.now();

      // Update segments in message
      message.segments = Array.from(context.segments.values());
    }

    // Emit completion event
    this._emit("onComplete", context.messageId, {
      finishReason,
      usage: context.totalUsage,
      segments: context.segments,
    });
    logger.log(
      { tags: "ai-inference|debug|complete", color1: "blue" },
      "[ON INFERENCE FINISHED DEBUG] _handleCompletion invoked",
      {
        messageId: context.messageId,
        finishReason,
        recursionDepth: context.recursionDepth,
      }
    );
    this._emitInferenceFinished(context, {
      finishReason,
      usage: context.totalUsage,
      segments: context.segments,
    });

    logger.log(
      {
        tags: "ai-inference|complete",
        color1: "green",
      },
      `Inference completed: ${finishReason}`
    );
  }

  /**
   * Handle a unified `inference_error` sidecar chunk from the backend (or a legacy
   * `abnormal_termination` chunk routed here). Normalizes into the shared envelope
   * and funnels through `_handleError` so the UI has one error surface.
   *
   * @param {Object} parsedChunk - Revolt sidecar chunk (must include `envelope` for
   *                                new-style, or finishReason/terminationReason for legacy).
   * @param {Object} context - Inference context
   */
  _handleInferenceErrorChunk(parsedChunk, context) {
    // Prefer the explicit envelope shipped with new-style chunks; fall back to the
    // whole chunk so `normalizeInferenceError` can decode the legacy
    // abnormal_termination format.
    const source = parsedChunk?.envelope || parsedChunk;
    const normalized = normalizeInferenceError(source);

    // Stamp the message with error + (optional) diagnostics so downstream UI
    // components (e.g. AIChatInterface banner, bubble overlays) can render
    // contextual info without re-parsing the chunk.
    const message = this.chat.getMessage(context.messageId);
    if (message) {
      message.inferenceError = {
        errorCode: normalized.revolt.errorCode,
        phase: normalized.revolt.phase,
        userMessage: normalized.revolt.userMessage,
        retryable: normalized.revolt.retryable,
        provider: normalized.provider || null,
        diagnostics: normalized.diagnostics || null,
        timestamp: Date.now(),
      };
      // Preserve legacy `abnormalTermination` shape for any component still
      // reading it. Remove after the one-release migration window.
      if (
        parsedChunk.legacyType === "abnormal_termination" ||
        parsedChunk.type === "abnormal_termination"
      ) {
        message.abnormalTermination = {
          reason: parsedChunk.terminationReason,
          receivedFinishReason: parsedChunk.receivedFinishReason,
          receivedUsage: parsedChunk.receivedUsage,
          receivedDone: parsedChunk.receivedDone,
          totalChunks: parsedChunk.totalChunks,
          finishReason: parsedChunk.finishReason,
          usage: parsedChunk.usage,
          timestamp: Date.now(),
        };
      }
    }

    logger.error(
      { tags: "ai-inference|inference-error", color1: "red" },
      `Inference error sidecar: ${normalized.revolt.errorCode} (${normalized.revolt.phase})`,
      {
        messageId: context.messageId,
        userMessage: normalized.revolt.userMessage,
        provider: normalized.provider,
        diagnostics: normalized.diagnostics,
      }
    );

    // Fire the new event for listeners that want structured access...
    this._emit("onInferenceError", context.messageId, normalized);
    // ...and also keep firing the legacy event when applicable so existing
    // listeners in subclasses / binders don't silently stop receiving.
    if (
      parsedChunk.legacyType === "abnormal_termination" ||
      parsedChunk.type === "abnormal_termination"
    ) {
      this._emit("onAbnormalTermination", context.messageId, {
        terminationReason: parsedChunk.terminationReason,
        receivedFinishReason: parsedChunk.receivedFinishReason,
        receivedUsage: parsedChunk.receivedUsage,
        receivedDone: parsedChunk.receivedDone,
        totalChunks: parsedChunk.totalChunks,
        finishReason: parsedChunk.finishReason,
        usage: parsedChunk.usage,
      });
    }

    // Convert envelope to an Error and funnel through _handleError. This reuses
    // the existing "inference is finished with an error" plumbing (updates
    // message.error, emits onError, emits onInferenceFinished exactly once).
    const err = errorFromEnvelope(normalized);
    this._handleError(context, err);
  }

  /**
   * Handle errors during inference
   * @param {Object} context - Inference context
   * @param {Error} error - Error that occurred
   */
  _handleError(context, error) {
    // End any remaining segments
    this._endSegment(context);

    // Purge in-flight tool calls using the same path as user cancellation.
    // An error during generation leaves partial / invalid tool_calls behind;
    // we want the transcript and UI to behave exactly like a cancel so the
    // user can safely "resume" without broken history. Executing tools are
    // left executing (their completions still flow normally) -- same as cancel.
    this._emitToolCallCleanupForCancelled(context);

    // Update message with error state AFTER purge so message.segments reflects
    // the cleaned context (no dangling streaming tool-call segments).
    const message = this.chat.getMessage(context.messageId);
    if (message) {
      message.error = error.message;
      message.completedAt = Date.now();
      message.segments = Array.from(context.segments.values());
    }

    // Emit error event
    this._emit("onError", context.messageId, error);
    logger.log(
      { tags: "ai-inference|debug|error", color1: "blue" },
      "[ON INFERENCE FINISHED DEBUG] _handleError invoked",
      {
        messageId: context.messageId,
        error: error?.message || "unknown",
        recursionDepth: context.recursionDepth,
      }
    );
    this._emitInferenceFinished(context, {
      error,
      usage: context.totalUsage,
      segments: context.segments,
    });

    logger.error(
      {
        tags: "ai-inference|error",
        color1: "red",
      },
      "Inference error:",
      error
    );
  }

  /**
   * Emit a singular inference-finished event exactly once per session
   * @param {Object} context - Inference context
   * @param {Object} result - Completion details
   */
  _emitInferenceFinished(context, result = {}) {
    if (!context || context.hasEmittedInferenceFinished) {
      return;
    }
    context.hasEmittedInferenceFinished = true;

    const checkpoint = this._finalizeCheckpointForContext(context);
    if (checkpoint) {
      this.onChatModified("checkpointFinalized", {
        checkpointId: checkpoint.id,
        responseMessageId: checkpoint.responseMessageId,
      });
    }

    logger.log(
      {
        tags: "ai-inference|debug|finished",
        color1: "blue",
      },
      "[ON INFERENCE FINISHED DEBUG] _emitInferenceFinished invoked",
      {
        messageId: context.messageId,
        inferenceSessionId: context.inferenceSessionId,
        finishReason: result.finishReason,
        usage: result.usage,
        error: result.error,
        abnormalTermination: result.abnormalTermination,
        segmentsCount: result.segments
          ? result.segments.size
          : context.segments?.size,
        totalSegmentsCount: context.totalSegmentsCount,
        wasCancelled: result.finishReason === "cancelled",
        wasSuccessful:
          !result.error &&
          !result.abnormalTermination &&
          result.finishReason !== "cancelled",
        completedAt: Date.now(),
        recursionDepth: context.recursionDepth,
      }
    );

    this._emit("onInferenceFinished", context.messageId, {
      inferenceSessionId: context.inferenceSessionId,
      rootResponseMessageId: context.rootResponseMessageId || context.messageId,
      finishReason: result.finishReason || "unknown",
      usage: result.usage || context.totalUsage,
      error: result.error || null,
      abnormalTermination: result.abnormalTermination || null,
      segments: result.segments || context.segments,
      wasCancelled: result.finishReason === "cancelled",
      wasSuccessful:
        !result.error && !result.abnormalTermination && result.finishReason !== "cancelled",
      completedAt: Date.now(),
      recursionDepth: context.recursionDepth,
    });
  }

  _finalizeCheckpointForContext(context) {
    const responseMessageId =
      context?.rootResponseMessageId || context?.messageId;
    const entryMap = this._getResponseEntryMap(responseMessageId, false);
    if (!entryMap || entryMap.size === 0) {
      return null;
    }
    if (!this.chat) {
      return null;
    }
    const checkpointId = `chk_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    const entries = {};
    for (const [entryId, entry] of entryMap.entries()) {
      entries[entryId] = entry;
    }

    const checkpoint = {
      id: checkpointId,
      responseMessageId,
      inferenceSessionId: context.inferenceSessionId || null,
      createdAt: Date.now(),
      entries,
    };

    if (!Array.isArray(this.chat.checkpointOrder)) {
      this.chat.checkpointOrder = [];
    }
    if (!this.chat.checkpointsById || typeof this.chat.checkpointsById !== "object") {
      this.chat.checkpointsById = {};
    }
    this.chat.checkpointOrder.push(checkpointId);
    this.chat.checkpointsById[checkpointId] = checkpoint;

    this.clearResponseEntries(responseMessageId);
    return checkpoint;
  }

  async restoreCheckpoint(checkpointId) {
    try {
      const chat = this.chat;
      if (!chat?.checkpointsById || !Array.isArray(chat.checkpointOrder)) {
        return { success: false, error: "Checkpoint system not initialized" };
      }
      const targetCheckpoint = chat.checkpointsById[checkpointId];
      if (!targetCheckpoint) {
        return { success: false, error: "Checkpoint not found in current chat" };
      }
      const startIndex = chat.checkpointOrder.indexOf(checkpointId);
      if (startIndex < 0) {
        return { success: false, error: "Checkpoint not found in current chat" };
      }

      const entriesById = new Map();
      const entriesToRestore = [];
      for (let index = startIndex; index < chat.checkpointOrder.length; index += 1) {
        const id = chat.checkpointOrder[index];
        const checkpoint = chat.checkpointsById[id];
        if (!checkpoint?.entries) {
          continue;
        }
        for (const [entryId, entry] of Object.entries(checkpoint.entries)) {
          if (!entriesById.has(entryId)) {
            entriesById.set(entryId, entry);
            entriesToRestore.push(entry);
          }
        }
      }

      if (entriesToRestore.length === 0) {
        return { success: true, restored: 0, redoCheckpointId: null };
      }

      const redoCheckpointId = `redo_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 10)}`;
      this.activeRedoCheckpointId = redoCheckpointId;
      this.activeRedoEntriesById = new Map();
      const restoredToolIds = [];

      // Process in reverse chronological order (newest operations first) so undo
      // runs in correct sequence: undelete → unmove → unedit → unwrite → uncreate
      entriesToRestore.reverse();
      for (const entry of entriesToRestore) {
        const toolId = entry?.toolId;
        if (typeof toolId === "string" && toolId.length > 0) {
          restoredToolIds.push(toolId);
        }
        const tool =
          this.aiToolSystem?.getToolInstance?.(toolId) || null;
        if (!tool?.restoreEntry) {
          throw new Error(
            `No restoreEntry() implemented for tool: ${toolId || "unknown"}`
          );
        }
        await tool.restoreEntry(entry);
      }

      const redoEntries = {};
      for (const [entryId, entry] of this.activeRedoEntriesById.entries()) {
        redoEntries[entryId] = entry;
      }

      if (Object.keys(redoEntries).length > 0) {
        if (!Array.isArray(chat.redoStash)) {
          chat.redoStash = [];
        }
        if (!chat.redoById || typeof chat.redoById !== "object") {
          chat.redoById = {};
        }
        chat.redoStash.push(redoCheckpointId);
        chat.redoById[redoCheckpointId] = {
          id: redoCheckpointId,
          sourceCheckpointId: checkpointId,
          createdAt: Date.now(),
          entries: redoEntries,
        };
      }

      // Notify that chat was modified by restore operation
      this.onChatModified("restoreCheckpoint", {
        checkpointId,
        restored: entriesById.size,
        restoredToolIds,
        redoCheckpointId: Object.keys(redoEntries).length > 0 ? redoCheckpointId : null,
      });

      return {
        success: true,
        restored: entriesById.size,
        redoCheckpointId:
          Object.keys(redoEntries).length > 0 ? redoCheckpointId : null,
      };
    } catch (error) {
      return { success: false, error: error.message || "Restore failed" };
    } finally {
      this.activeRedoCheckpointId = null;
      this.activeRedoEntriesById = null;
    }
  }

  async restoreRedoCheckpoint(redoCheckpointId) {
    try {
      const chat = this.chat;
      if (!chat?.redoById) {
        return { success: false, error: "Redo system not initialized" };
      }
      const redoCheckpoint = chat.redoById[redoCheckpointId];
      if (!redoCheckpoint) {
        return { success: false, error: "Redo checkpoint not found in current chat" };
      }

      const entries = redoCheckpoint.entries || {};
      const entryList = Object.values(entries);
      if (entryList.length === 0) {
        return { success: true, restored: 0 };
      }
      const restoredToolIds = [];

      // Redo must re-apply operations in chronological order (create → write → move → delete)
      entryList.reverse();
      for (const entry of entryList) {
        const toolId = entry?.toolId;
        if (typeof toolId === "string" && toolId.length > 0) {
          restoredToolIds.push(toolId);
        }
        const tool = this.aiToolSystem?.getToolInstance?.(toolId) || null;
        if (!tool?.restoreRedoEntry) {
          throw new Error(
            `No restoreRedoEntry() implemented for tool: ${toolId || "unknown"}`
          );
        }
        await tool.restoreRedoEntry(entry);
      }

      // Clear redo entry now that it has been applied
      if (Array.isArray(chat.redoStash)) {
        chat.redoStash = chat.redoStash.filter((id) => id !== redoCheckpointId);
      }
      if (chat.redoById && typeof chat.redoById === "object") {
        delete chat.redoById[redoCheckpointId];
      }

      // Notify that chat was modified by redo operation
      this.onChatModified("restoreRedoCheckpoint", {
        redoCheckpointId,
        restored: entryList.length,
        restoredToolIds,
      });

      return { success: true, restored: entryList.length };
    } catch (error) {
      return { success: false, error: error.message || "Redo restore failed" };
    }
  }

  /**
   * Destroy the inference system and clean up resources
   */
  destroy() {
    // Cancel any active inferences
    for (const context of this.activeContexts) {
      if (context.cancelController) {
        context.cancelController.abort();
      }
    }
    this.activeContexts.clear();

    // Clear event listeners
    this.removeAllListeners();

    // Clear references
    this.aiToolSystem = null;
    this.checkpointEntriesByResponse.clear();
    this.activeToolContext = null;
    this.activeRedoCheckpointId = null;
    this.activeRedoEntriesById = null;
  }
}
